<template>
  <section class="coupon-editor">
    <div class="input-container">
      <label class="label">รหัสส่วนลด</label>
      <el-input class="input" v-model="value.code" :disabled="value.isActionEdit"></el-input>
    </div>
    <div class="input-container">
      <label class="label">ประเภทส่วนลด</label>
      <el-radio
        :key="type.value"
        v-for="type in couponType"
        v-model="selectedCouponType"
        :label="type.value"
        :disabled="value.isActionEdit"
      >{{ type.label }}</el-radio>
    </div>
    <div class="input-container">
      <label class="label">ยอดลด</label>
      <el-input
        class="input"
        v-model="discount"
        type="number"
        min="0"
        :disabled="value.isActionEdit"
      ></el-input>
      {{ discountUnit }}
    </div>
    <div class="input-container">
      <label class="label">จำนวนสิทธิ์</label>
      <el-checkbox v-model="quotaNoLimit">ไม่จำกัด</el-checkbox>
      <el-input
        class="input"
        v-if="!quotaNoLimit"
        v-model="usageQuota"
        type="number"
        min="0"
        :disabled="value.isActionEdit"
      ></el-input>
    </div>
    <div class="input-container">
      <label class="label">จำนวนสิทธิ์ต่อคน</label>
      <el-checkbox v-model="quotaPerPersonDefault">ค่าเริ่มต้น 1 สิทธิ</el-checkbox>
      <el-input
        class="input"
        v-if="!quotaPerPersonDefault"
        v-model="usageQuotaPerUser"
        type="number"
        min="1"
        :disabled="value.isActionEdit"
      ></el-input>
    </div>
    <div class="input-container">
      <label class="label">&nbsp;</label>
      <el-checkbox
        v-model="isFirstTimeOnly"
        :disabled="value.isActionEdit"
      >สำหรับลูกค้าซื้อครั้งแรกเท่านั้น
      </el-checkbox>
    </div>
    <div class="input-container">
      <div>
        <label class="label">ระยะเวลาใช้งาน</label>
        <label class="usage-date-range-label">วันที่เริ่ม</label>
        <date-picker
          v-model="effectiveDate"
          :disabled="value.isActionEdit"
        ></date-picker>
      </div>
      <div>
        <label class="label">&nbsp;</label>
        <el-radio v-model="periodType" label="expiryDate" class="usage-date-range-label">วันที่สิ้นสุด</el-radio>
        <date-picker
          class="top-margin"
          v-model="expiryDate"
          :disabled="periodType !== 'expiryDate' || isExpired"
        ></date-picker>
      </div>
      <div>
        <label class="label">&nbsp;</label>
        <el-radio
          v-model="periodType"
          label="transferDate"
          :disabled="value.isActionEdit"
          class="usage-date-range-label"
        >นับจากวันที่โอน</el-radio>
        <el-input
          class="input top-margin"
          type="number"
          v-model="expiredDaysAfterTransfered"
          :disabled="periodType !== 'transferDate'"
        ></el-input> วัน
      </div>
    </div>
    <div class="input-container">
      <label class="label">ยอดซื้อขั้นต่ำ</label>
      <el-input
        class="input"
        v-model="minimumPurchasedAmount"
        type="number"
        :disabled="value.isActionEdit"
        min="0"
      ></el-input>
      {{ "บาท" }}
    </div>
    <div class="input-container" v-if="showMaximumDiscountAmount">
      <label class="label">ยอดลดสูงสุด</label>
      <el-input
        class="input"
        v-model="maximumDiscountAmount"
        type="number"
        min="0"
        :disabled="value.isActionEdit"
      ></el-input>
      {{ "บาท" }}
    </div>
    <div class="input-container row">
      <label class="label col-12">แพ็กเกจที่ร่วมรายการ</label>
      <package-selector
        v-model="selectedPackages"
        :isActionEdit="value.isActionEdit"
        :excludedPackages="excludedPackages"
        :isCouponPage="true"
      >
      </package-selector>
    </div>
    <div class="input-container">
      <label class="label">โครงการที่รองรับ</label>
      <br />
      <el-checkbox v-model="isSupportAllProjects">รองรับทุกโครงการ</el-checkbox>
      <project-selector
        v-show="!isSupportAllProjects"
        :value="supportedProjects"
        :disableDeleteAll="disableProjectDeleteAll"
        @input="supportedProjectsChange"
      ></project-selector>
    </div>
  </section>
</template>

<script>
import {
  Input as ElInput,
  Radio as ElRadio,
  Checkbox as ElCheckbox
} from "element-ui";
import ScButton from "@/lib/components/ScButton";
import DatePicker from "@/lib/components/DatePicker";
import PackageSelector from "../../collections/components/PackageSelector";
import ProjectSelector from "@/lib/components/ProjectSelector";

export default {
  props: ["value", "disableProjectDeleteAll"],
  components: {
    ScButton,
    ElInput,
    ElRadio,
    ElCheckbox,
    DatePicker,
    PackageSelector,
    ProjectSelector
  },
  data() {
    const couponType = [
      { label: "ส่วนลดเป็นจำนวนเงิน", value: "flat" },
      { label: "ส่วนลดเป็น %", value: "percentage" }
    ];

    const {
      discountAmount,
      discountPercentAmount,
      usageQuota,
      usageQuotaPerUser,
      selectedPackages,
      supportedProjectIds,
      effectiveDate,
      expiryDate,
      expiredDaysAfterTransfered,
      maximumDiscountAmount,
      minimumPurchasedAmount,
      isFirstTimeOnly,
      isExpired,
      excludedPackages
    } = this.value;

    const selectedCouponType = discountPercentAmount ? "percentage" : "flat";
    const discount =
      selectedCouponType === "flat"
        ? discountAmount / 100
        : discountPercentAmount;
    const quotaNoLimit = !Boolean(usageQuota);
    const quotaPerPersonDefault = !Boolean(usageQuotaPerUser);
    const isSupportAllProjects = !Boolean(supportedProjectIds);
    let periodType;
    if (expiredDaysAfterTransfered !== null) {
      periodType = 'transferDate';
    } else {
      periodType = 'expiryDate';
    }

    return {
      couponType,
      selectedCouponType,
      discount,
      quotaNoLimit,
      quotaPerPersonDefault,
      selectedPackages,
      effectiveDate,
      expiryDate,
      expiredDaysAfterTransfered,
      maximumDiscountAmount:
        maximumDiscountAmount && maximumDiscountAmount !== 0
          ? maximumDiscountAmount / 100
          : maximumDiscountAmount,
      minimumPurchasedAmount:
        minimumPurchasedAmount && minimumPurchasedAmount !== 0
          ? minimumPurchasedAmount / 100
          : minimumPurchasedAmount,
      usageQuota,
      usageQuotaPerUser,
      isSupportAllProjects,
      periodType,
      isFirstTimeOnly,
      isExpired,
      excludedPackages
    };
  },
  watch: {
    selectedCouponType(value) {
      this.selectedCouponTypeChange(value);
    },
    discount(value) {
      this.discountChange(value);
    },
    quotaNoLimit(value) {
      this.quotaNoLimitChange(value);
    },
    quotaPerPersonDefault(value) {
      this.quotaPerPersonDefaultChange(value);
    },
    selectedPackages(value) {
      this.selectedPackagesChange(value);
    },
    minimumPurchasedAmount(value) {
      this.numberValueChange("minimumPurchasedAmount", value * 100);
    },
    maximumDiscountAmount(value) {
      this.numberValueChange("maximumDiscountAmount", value * 100);
    },
    usageQuota(value) {
      this.numberValueChange("usageQuota", value);
    },
    usageQuotaPerUser(value) {
      this.numberValueChange("usageQuotaPerUser", value);
    },
    isSupportAllProjects(value) {
      this.isSupportAllProjectsChange(value);
    },
    effectiveDate(value) {
      this.effectiveDateChange(value);
    },
    expiryDate(value) {
      this.expiryDateChange(value);
    },
    expiredDaysAfterTransfered(value) {
      this.numberValueChange("expiredDaysAfterTransfered", value);
    },
    periodType(value) {
      this.expiredDaysAfterTransfered = null;
      this.expiryDate = null;
    },
    isFirstTimeOnly(value) {
      const isFirstTimeOnly = value;
      this.$emit("input", {
        ...this.value,
        isFirstTimeOnly
      });
    }
  },
  computed: {
    discountUnit() {
      const unit = {
        flat: "บาท",
        percentage: "%"
      };

      return unit[this.selectedCouponType];
    },
    showMaximumDiscountAmount() {
      return !this.isFlatRate;
    },
    isFlatRate() {
      return this.selectedCouponType === this.couponType[0].value;
    },
    supportedProjects() {
      return (this.value.supportedProjectIds || []).map(projectId => ({
        projectId
      }));
    }
  },
  mounted() {},
  methods: {
    selectedCouponTypeChange(value) {
      if (this.discount === 0) return;

      this.discount = 0;
    },
    discountChange(discount) {
      const value = this.isFlatRate
        ? {
            ...this.value,
            discountAmount: +discount * 100,
            discountPercentAmount: undefined
          }
        : {
            ...this.value,
            discountAmount: undefined,
            discountPercentAmount: +discount
          };

      this.$emit("input", value);
    },
    quotaNoLimitChange(isNoLimit) {
      if (isNoLimit) {
        this.$emit("input", { ...this.value, usageQuota: undefined });
      }
    },
    quotaPerPersonDefaultChange(flag) {
      if (flag) {
        this.$emit("input", { ...this.value, usageQuotaPerUser: undefined });
      }
    },
    selectedPackagesChange(packages) {
      this.$emit("input", {
        ...this.value,
        selectedPackages: packages
      });
    },
    supportedProjectsChange(projects) {
      if (projects) {
        this.$emit("input", {
          ...this.value,
          supportedProjectIds: projects.map(({ projectId }) => projectId)
        });
        return;
      }

      this.$emit("input", { ...this.value, supportedProjectIds: null });
    },
    effectiveDateChange(effectiveDate) {
      this.$emit("input", {
        ...this.value,
        effectiveDate
      });
    },
    expiryDateChange(expiryDate) {
      expiryDate = expiryDate.setZone("utc+7").endOf("day").toUTC()
      this.$emit("input", {
        ...this.value,
        expiryDate
      });
    },
    numberValueChange(key, value) {
      this.$emit("input", { ...this.value, [key]: value ? +value : value });
    },
    isSupportAllProjectsChange(isSupportAllProjects) {
      if (isSupportAllProjects) {
        this.supportedProjectsChange(null);
        return;
      }

      this.supportedProjectsChange([]);
    }
  }
};
</script>

<style lang="scss" scoped>
.label {
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;

  width: 150px;

  &.sub {
    font-weight: lighter;
  }
}

.top-margin {
  margin-top: 5px;
}


.input {
  width: 100px;
}

.input-container {
  margin-bottom: 15px;
}

.usage-date-range-label {
  width: 130px;
  margin-right: 0px;
}
</style>



// WEBPACK FOOTER //
// src/features/subscriptions/modules/coupons/components/CouponEditor.vue